/*
 * fixtures.js — a local mirror of the eight real Notion schemas, clearly DEMO
 * data. Used only when the live backend is unavailable or for isolated tests
 * (per approved refinement #1). The UI shows a visible DEMO tag whenever this
 * is the active source, so demo content is never mistaken for real data
 * (Design System v1.0 — "lock the design, not the sample data").
 *
 * IDs are fixture:// URLs so relation shapes match Notion's page-URL arrays.
 *
 * BUILD 002 — U-3: every value below is UNMISTAKABLY FICTIONAL.
 *
 * Build 001 shipped invented data whose names nonetheless read like ordinary
 * street addresses, real towns and real people. Invented is not the same as
 * obviously invented: a tester glancing at the Owner board could reasonably
 * have taken a row for a real crew on a real site. Every worker is now
 * "Test Worker N", every project a "Test Site <letter>", and every address is
 * in the non-existent town of Testville, ZZ — "ZZ" is not a US state code, so
 * no address here can be mistaken for somewhere a crew could be sent.
 *
 * The tenant is fictional too. The previous alias carried the LIVE tenant id
 * so the printed QR stayed testable; a fixture-only build has no business
 * shipping a real operational identifier, so it is replaced with a fictional
 * one. Recorded in the Build 002 report as a temporary assumption — restoring
 * it is a one-line change, and scripts/verify-fixture-lock.mjs now fails the
 * build if the real id ever reappears in the shipped bundle.
 *
 * No real employee, project, address, or payroll value appears anywhere in
 * this file.
 */
import { todayISO, tomorrowISO } from './workdate.js'

export const IS_DEMO = true

// The one accepted demo company code. Lookup is case-insensitive and
// whitespace-trimmed ("testco", " TESTCO ", "TestCo" all resolve here);
// anything else is rejected.
export const TENANT = {
  code: 'TESTCO',
  id: 'fixture://tenant/testco',
  // A QR payload carries a tenant id directly; this fictional alias keeps that
  // path exercisable without shipping a real one.
  aliases: ['test-construction-co'],
  name: 'Test Construction Co.',
}

export const PROJECTS = [
  { id: 'fixture://project/site-alpha',   name: 'Test Site Alpha',   location: '100 Example Way, Testville, ZZ', status: 'Active' },
  { id: 'fixture://project/site-bravo',   name: 'Test Site Bravo',   location: '200 Example Way, Testville, ZZ', status: 'Active' },
  { id: 'fixture://project/site-charlie', name: 'Test Site Charlie', location: '300 Example Way, Testville, ZZ', status: 'Active' },
  { id: 'fixture://project/site-delta',   name: 'Test Site Delta',   location: '400 Example Way, Testville, ZZ', status: 'Active' },
]

export const EMPLOYEES = [
  { id: 'fixture://employee/worker-1', name: 'Test Worker One',   role: 'Team Member', active: true },
  { id: 'fixture://employee/worker-2', name: 'Test Worker Two',   role: 'Team Member', active: true },
  { id: 'fixture://employee/worker-3', name: 'Test Worker Three', role: 'Team Member', active: true },
  { id: 'fixture://employee/worker-4', name: 'Test Worker Four',  role: 'Team Member', active: true },
  { id: 'fixture://employee/worker-5', name: 'Test Worker Five',  role: 'Team Member', active: true },
  // Test Worker Six is deliberately present with NO assignment of any kind —
  // proof that the roster gate still lets an unassigned person through to the
  // typed-name path rather than making them disappear from the world.
  { id: 'fixture://employee/worker-6', name: 'Test Worker Six',   role: 'Team Member', active: true },
  // Non-team-member, present to prove role is NOT the eligibility filter.
  { id: 'fixture://employee/supervisor-1', name: 'Test Supervisor One', role: 'Superintendent', active: true },
]

/*
 * Project Assignment — Employee ↔ Project.
 *
 * BUILD 001 adds `workDate` (YYYY-MM-DD): one record per employee + project +
 * work date. Approved as the temporary v0 scheduling model — deliberately not
 * a range and not a recurrence rule, because one-record-per-day is the shape
 * that can be replaced later without migrating anything.
 *
 * `status: 'Active'` is the existing Activity Status field and is unchanged.
 * A record with no `workDate` is a STANDING assignment: it has no date and is
 * never today's expectation, but it still counts for roster eligibility. That
 * is what keeps an un-scheduled worker from vanishing off the name list.
 */
export const ASSIGNMENTS = [
  // Standing assignments (no work date) — pre-Build-001 shape, still valid.
  { id: 'fixture://assign/1', employeeId: 'fixture://employee/worker-4', projectId: 'fixture://project/site-delta', status: 'Active' },
  { id: 'fixture://assign/2', employeeId: 'fixture://employee/worker-5', projectId: 'fixture://project/site-charlie', status: 'Active' },

  // Dated assignments — today.
  { id: 'fixture://assign/10', employeeId: 'fixture://employee/worker-1', projectId: 'fixture://project/site-alpha', status: 'Active', workDate: todayISO() },
  { id: 'fixture://assign/11', employeeId: 'fixture://employee/worker-2', projectId: 'fixture://project/site-bravo', status: 'Active', workDate: todayISO() },
  { id: 'fixture://assign/12', employeeId: 'fixture://employee/worker-3', projectId: 'fixture://project/site-charlie', status: 'Active', workDate: todayISO() },

  // Dated assignments — tomorrow. Worker One moves; Worker Two stays put.
  { id: 'fixture://assign/20', employeeId: 'fixture://employee/worker-1', projectId: 'fixture://project/site-charlie', status: 'Active', workDate: tomorrowISO() },
  { id: 'fixture://assign/21', employeeId: 'fixture://employee/worker-2', projectId: 'fixture://project/site-bravo', status: 'Active', workDate: tomorrowISO() },
]
